import React, { useEffect, useRef, useState } from 'react';
import styles from './phonedialer.module.css';
import { getFetch, postFetch } from '../../fetch/fetch';
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Select from '@mui/joy/Select';
import Option from '@mui/joy/Option';
import { CssVarsProvider } from '@mui/joy/styles';

// 전화걸기 패널 — 자체 교환기(FreePBX) 웹 발신 버전
// 기존 phonedialer.tsx(HappyTalk) 와 UI 동일, 차이: 멘트 섹션 없음(교환기가 항상 재생), 통화 끊기 없음(발신 수락 여부만 응답)
// 지점·내선 목록은 options API 응답만 사용 (하드코딩 금지). 마지막 선택은 패널이 언마운트되지 않으므로 세션 동안 유지된다

const NUMPAD = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '다시입력', '0', '⌫'];

const toastOptions : any = {
    position: "bottom-right",
    autoClose: 3000,
    hideProgressBar: true,
    theme: "colored"
};

interface Branch {
    queue: string;
    name: string;
    displayNumber: string;
}

interface Agent {
    extension: string;
    name: string;
}

interface UserProp {
    id?: number;
    name?: string;
    phoneNumber?: string;
    location?: string;
    value: string;
}

export interface DialerPrefill {
    customerNumber?: string | null;
    branchQueue?: string | null;
}

interface Props {
    open: boolean;
    onClose: () => void;
    user?: UserProp | null;
    prefill?: DialerPrefill | null;
}

// 서버 에러 코드 → 안내 문구
const errorText = (message : string) => {
    switch (message) {
        case 'INVALID_NUMBER': return '고객 번호를 확인해주세요';
        case 'INVALID_BRANCH':
        case 'INVALID_AGENT': return '지점/상담사를 다시 선택해주세요';
        case 'RATE_LIMIT': return '잠시 후 다시 시도해주세요 (5초)';
        case 'PBX_NOT_CONNECTED':
        case 'AMI_TIMEOUT':
        case 'AMI_ERROR':
        case 'CALLCENTER_DISABLED': return '교환기 연결 오류';
        case 'NOT_AUTHORIZED': return '권한이 없습니다';
        default: return '발신 실패: ' + message;
    }
};

const PhoneDialerPbx: React.FC<Props> = ({ open, onClose, user, prefill }) => {

    const [branches, setBranches] = useState<Branch[]>([]);
    const [agents, setAgents] = useState<Agent[]>([]);
    const [branchQueue, setBranchQueue] = useState('');
    const [agentExtension, setAgentExtension] = useState('');
    // 유선 전화기 등록 탭에서 설정한 내 기본값 (없으면 null)
    const [myDefault, setMyDefault] = useState<{ agentExtension: string; branchQueue: string | null } | null>(null);
    const [ani, setAni] = useState('010');
    const [loading, setLoading] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    // 패널 열릴 때마다 옵션 로드 (active 내선 변동 반영)
    useEffect(() => {
        if (!open) return;
        const load = async () => {
            try {
                const result: any = await getFetch('/dashboard/callcenter/outcall/options');
                if (result.message === 'success') {
                    setBranches(result.branches || []);
                    setAgents(result.agents || []);
                    setMyDefault(result.myDefault || null);
                } else {
                    toast.error(errorText(result.message), toastOptions);
                }
            } catch (e) {
                console.log(e);
                toast.error('교환기 연결 오류', toastOptions);
            }
        };
        load();
    }, [open]);

    // 지점 기본 선택: prefill(목록에 있을 때) > 현재 선택 > 설정된 기본값(myDefault) > user.location 앞 2글자 일치 > 첫 항목
    useEffect(() => {
        if (branches.length === 0) return;
        const has = (q : string) => branches.some(b => b.queue === q);
        if (prefill?.branchQueue && has(prefill.branchQueue)) {
            setBranchQueue(prefill.branchQueue);
            return;
        }
        if (branchQueue && has(branchQueue)) return;
        if (myDefault?.branchQueue && has(myDefault.branchQueue)) {
            setBranchQueue(myDefault.branchQueue);
            return;
        }
        const locationPrefix = user?.location?.slice(0, 2);
        const byLocation = locationPrefix ? branches.find(b => b.name.slice(0, 2) === locationPrefix) : undefined;
        setBranchQueue((byLocation || branches[0]).queue);
    }, [branches, prefill, myDefault]);

    // 내선 기본 선택: 현재 선택 > 설정된 기본값(myDefault) > 내 이름과 같은 수신자 > 첫 항목
    useEffect(() => {
        if (agents.length === 0) return;
        if (agentExtension && agents.some(a => a.extension === agentExtension)) return;
        if (myDefault?.agentExtension && agents.some(a => a.extension === myDefault.agentExtension)) {
            setAgentExtension(myDefault.agentExtension);
            return;
        }
        const byName = user?.name ? agents.find(a => a.name === user.name) : undefined;
        setAgentExtension((byName || agents[0]).extension);
    }, [agents, myDefault]);

    // 패널 열릴 때 번호 초기화(prefill 있으면 그 번호) + 포커스
    useEffect(() => {
        if (open) {
            const digits = (prefill?.customerNumber || '').replace(/\D/g, '').slice(0, 11);
            setAni(digits || '010');
            setTimeout(() => inputRef.current?.focus(), 0);
        }
    }, [open, prefill]);

    if (!open) return null;

    const handleNum = (key: string) => {
        if (key === '다시입력') {
            setAni('010');
        } else if (key === '⌫') {
            setAni(prev => prev.slice(0, -1));
        } else {
            if (ani.length >= 11) return;
            setAni(prev => prev + key);
        }
    };

    const formatDisplay = (num: string) => {
        if (num.length <= 3) return num;
        if (num.length <= 7) return `${num.slice(0, 3)}-${num.slice(3)}`;
        return `${num.slice(0, 3)}-${num.slice(3, 7)}-${num.slice(7)}`;
    };

    const selectedAgent = agents.find(a => a.extension === agentExtension);
    const canCall = ani.length >= 9 && !!branchQueue && !!agentExtension && !loading;

    const handleCall = async () => {
        if (!canCall) return;
        setLoading(true);
        try {
            const result: any = await postFetch('/dashboard/callcenter/outcall', {
                branchQueue,
                agentExtension,
                customerNumber: ani,
            });
            if (result.message === 'success') {
                const agentName = result.agentName || selectedAgent?.name || '상담사';
                toast.success(`발신 중 — ${agentName} 전화기가 울립니다. 받으면 고객에게 연결됩니다`, { ...toastOptions, autoClose: 5000 });
                setAni('010');
                onClose();
            } else {
                toast.error(errorText(result.message), toastOptions);
            }
        } catch (e) {
            console.log(e);
            toast.error('오류가 발생했습니다.', toastOptions);
        }
        setLoading(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (/^\d$/.test(e.key)) {
            e.preventDefault();
            if (ani.length < 11) setAni(prev => prev + e.key);
        } else if (e.key === 'Backspace') {
            e.preventDefault();
            setAni(prev => prev.slice(0, -1));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            handleCall();
        } else if (e.key !== 'Tab' && !(e.ctrlKey || e.metaKey)) {
            e.preventDefault();
        }
    };

    // 붙여넣기: 숫자만 추출해서 전체 교체 (하이픈·공백 포함 번호 대응)
    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        const digits = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 11);
        if (digits) setAni(digits);
    };

    return (
        <div className={styles.panel}>

            <div className={styles.header}>
                <span className={styles.title}>전화걸기</span>
                <button className={styles.closeBtn} onClick={onClose}>✕</button>
            </div>

            <div className={styles.section}>
                <div className={styles.label}>발신대표번호 설정</div>
                <CssVarsProvider>
                    <Select
                        value={branchQueue}
                        onChange={(_, val) => val && setBranchQueue(val as string)}
                        size="sm"
                        sx={{ width: '100%' }}
                        placeholder="지점 선택"
                    >
                        {branches.map(b => (
                            <Option key={b.queue} value={b.queue}>{b.name} ({b.displayNumber})</Option>
                        ))}
                    </Select>
                </CssVarsProvider>
            </div>

            <div className={styles.section}>
                <div className={styles.label}>상담사 번호</div>
                <CssVarsProvider>
                    <Select
                        value={agentExtension}
                        onChange={(_, val) => val && setAgentExtension(val as string)}
                        size="sm"
                        sx={{ width: '100%' }}
                        placeholder="내선 선택"
                    >
                        {agents.map(a => (
                            <Option key={a.extension} value={a.extension}>{a.name} ({a.extension})</Option>
                        ))}
                    </Select>
                </CssVarsProvider>
            </div>

            <input
                ref={inputRef}
                className={styles.displayInput}
                value={formatDisplay(ani)}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                onChange={() => {}}
                placeholder="고객 전화번호 입력"
            />

            <div className={styles.numpad}>
                {NUMPAD.map(key => (
                    <button
                        key={key}
                        className={`${styles.numKey} ${key === '다시입력' ? styles.numKeySmall : ''}`}
                        onClick={() => handleNum(key)}
                    >
                        {key}
                    </button>
                ))}
            </div>

            <button
                className={`${styles.callBtn} ${!canCall ? styles.callBtnDisabled : ''}`}
                onClick={handleCall}
                disabled={!canCall}
            >
                {loading ? '연결 중...' : '통화 걸기'}
            </button>

        </div>
    );
};

export default PhoneDialerPbx;
